import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  Info,
  AlertCircle,
  Activity,
  ShieldAlert,
  Timer,
  HelpCircle,
  Layout,
  BarChart3,
  FileText
} from 'lucide-react';
import styles from './SecurityScore.module.sass';

interface NavButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ icon, label, active, onClick }) => (
  <button
    className={`${styles.navButton} ${active ? styles.active : ''}`}
    onClick={onClick}
  >
    {icon}
    {label}
  </button>
);

interface SecurityScoreModalProps {
  open: boolean;
  onClose: () => void;
  score: number;
  label: string;
}

const SecurityScoreModal: React.FC<SecurityScoreModalProps> = ({ open, onClose, score, label }) => {
  const [activeTab, setActiveTab] = useState('overview');

  const getStatusColor = (score: number) => {
    if (score <= 25) return '#F121C3';
    if (score <= 50) return '#C458E3';
    if (score <= 75) return '#98A4FF';
    return '#73F6FF';
  };

  const getScoreDetails = (score: number) => {
    const safeScore = Number(score) || 0;
    
    if (safeScore >= 80) {
      return {
        category: 'OPTIMAL',
        icon: CheckCircle,
        description: 'This asset demonstrates strong security fundamentals.',
        recommendation: 'Standard security practices should be maintained.'
      };
    } else if (safeScore >= 60) {
      return {
        category: 'STABLE',
        icon: Shield,
        description: 'This asset shows acceptable security metrics with some areas for improvement.',
        recommendation: 'Monitor security indicators and exercise normal caution.'
      };
    } else if (safeScore >= 40) {
      return {
        category: 'COMPROMISED',
        icon: AlertTriangle,
        description: 'This asset has significant security concerns that require attention.',
        recommendation: 'Exercise increased caution and review security factors carefully.'
      };
    } else {
      return {
        category: 'CRITICAL',
        icon: XCircle,
        description: 'This asset presents severe security risks.',
        recommendation: 'Exercise extreme caution. Multiple security concerns have been identified.'
      };
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <div className={styles.infoSection}>
              <h3>
                <AlertCircle size={16} />
                Security Assessment
              </h3>
              <p>{details.description} {details.recommendation}</p>
            </div>
            <div className={styles.infoSection}>
              <h3>
                <Info size={16} />
                About Security Score
              </h3>
              <p>
                Our Safety Score is a bespoke metric designed to evaluate the security of ERC-20 tokens
                by analyzing multiple aspects of each token's smart contract. This comprehensive score
                leverages over 35 different factors, incorporating both on-chain and off-chain data.
              </p>
            </div>
          </>
        );
      case 'factors':
        return (
          <>
            <div className={styles.infoSection}>
              <h3>
                <AlertCircle size={16} />
                Key Security Factors
              </h3>
              <p>
                Our security analysis examines multiple critical factors to assess the overall safety
                of the token. Below are the key factors we evaluate:
              </p>
              <div className={styles.factorsList}>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Sell Tax Analysis
                </div>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Buy Tax Verification
                </div>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Tax Modification Checks
                </div>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Token Mintability
                </div>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Honeypot Detection
                </div>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Owner Token Percentage
                </div>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Airdrop Scam Detection
                </div>
                <div className={styles.factorItem}>
                  <Shield size={14} />
                  Phishing Activity Checks
                </div>
              </div>
            </div>
            <div className={styles.infoSection}>
              <h3>
                <Activity size={16} />
                Score Updates
              </h3>
              <p>
                Token safety scores are dynamic and updated regularly. Initial scores are assigned within
                minutes of the first trade, and reassessment occurs weekly or upon significant events.
              </p>
            </div>
          </>
        );
      case 'methodology':
        return (
          <>
            <div className={styles.infoSection}>
              <h3>
                <ShieldAlert size={16} />
                Scoring Methodology
              </h3>
              <p>
                Our scoring system employs advanced analytics to assess token security across multiple
                dimensions. We analyze smart contract code, transaction patterns, and market behavior
                to generate comprehensive security ratings.
              </p>
            </div>
            <div className={styles.infoSection}>
              <h3>
                <Timer size={16} />
                Update Frequency
              </h3>
              <p>
                Scores remain valid for one week after initial analysis. Re-analysis is triggered by
                new trades or significant events. Tokens without recent trading activity maintain their
                last valid score until new activity occurs.
              </p>
            </div>
            <div className={styles.infoSection}>
              <h3>
                <HelpCircle size={16} />
                Data Requirements
              </h3>
              <p>
                Tokens require sufficient on-chain data for accurate scoring. Those with insufficient
                data receive a default score of '0' until adequate information becomes available.
              </p>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const color = getStatusColor(score);
  const details = getScoreDetails(score);
  const Icon = details.icon;

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        className: styles.modalPaper
      }}
      maxWidth={false}
    >
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <div className={styles.header}>
            <div className={styles.title}>{label}</div>
            <div className={styles.subtitle}>Security Analysis</div>
          </div>
          
          <div className={styles.nav}>
            <NavButton
              icon={<Layout size={18} />}
              label="Overview"
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
            />
            <NavButton
              icon={<BarChart3 size={18} />}
              label="Security Factors"
              active={activeTab === 'factors'}
              onClick={() => setActiveTab('factors')}
            />
            <NavButton
              icon={<FileText size={18} />}
              label="Methodology"
              active={activeTab === 'methodology'}
              onClick={() => setActiveTab('methodology')}
            />
          </div>

          <div className={styles.scoreSection}>
            <div className={styles.scoreCircle}>
              <Icon color={color} size={32} style={{ marginBottom: '8px' }} />
              <div className={styles.scoreNumber}>{score}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(115, 246, 255, 0.8)' }}>/100</div>
            </div>
            
            <div className={styles.scoreInfo}>
              <div className={styles.category} style={{ color }}>
                {details.category}
              </div>
              <div className={styles.description}>
                {details.description}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.main}>
          <div className={styles.content}>
            {renderContent()}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default SecurityScoreModal;